import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	signal,
	Signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { WatchListAssetInterface } from '@modules/watch-list-module/interfaces/watch-list-asset.interface'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { WatchListColumnsEnum } from '@shared/components/trading/widgets/watch-list-widget/core/enums/watch-list-columns.enum'
import { WatchListResponseInterface } from '@modules/watch-list-module/interfaces/watch-list-response.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { PricePipe } from '@shared/pipes/price.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { WatchListActionsComponent } from '@shared/components/trading/watch-list/common/watch-list-actions/watch-list-actions.component'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { IconsByMarketStatusMap } from '@modules/alpaca-module/market-status/constants/icons-by-market-status-map'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { ImageInterface } from '@shared/interfaces/image.interface'
import { TranslatePipe } from '@ngx-translate/core'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { Subject, takeUntil } from 'rxjs'
import { ThemeService } from '@modules/theme-module/services/theme.service'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { SearchAssetsDialogComponent } from '@shared/components/trading/search-assets-dialog/search-assets-dialog.component'
import { TuiDialog } from '@taiga-ui/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { WatchListSettingsInterface } from '@shared/components/trading/widgets/watch-list-widget/core/interfaces/watch-list-settings.interface'
import { ColumnSortInterface } from '@shared/components/common/table/core/interfaces/column-sort.interface'
import { TableComponent } from '@shared/components/common/table/table.component'
import { MarketStatusInterface } from '@modules/alpaca-module/market-status/interfaces/market-status.interface'

@Component({
	selector: 'app-watch-list-table',
	templateUrl: 'watch-list-table.component.html',
	styleUrl: 'watch-list-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TableColumnComponent,
		CompanyLogoComponent,
		PricePipe,
		ProfitPipe,
		HighlightDirective,
		TableActionsComponent,
		WatchListActionsComponent,
		TranslatePipe,
		TableFooterComponent,
		ButtonComponent,
		SearchAssetsDialogComponent,
		TuiDialog,
		TableComponent,
	],
})
export class WatchListTableComponent implements OnInit, OnDestroy {
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly watchListColumnsEnum: typeof WatchListColumnsEnum = WatchListColumnsEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly iconsByMarketStatusMap: Map<MarketStatusEnum | null, ImageInterface> = IconsByMarketStatusMap

	public widget: InputSignal<WidgetInterface<WatchListSettingsInterface> | null> =
		input<WidgetInterface<WatchListSettingsInterface> | null>(null)
	public marketStatus: InputSignal<MarketStatusInterface | null> = input<MarketStatusInterface | null>(null)
	public watchList: InputSignal<WatchListResponseInterface | null> = input<WatchListResponseInterface | null>(null)
	public isLoading: InputSignal<boolean> = input<boolean>(false)
	public statesColumns: InputSignal<ColumnInterface<WatchListColumnsEnum>[]> = input<
		ColumnInterface<WatchListColumnsEnum>[]
	>([])

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)

	protected theme: WritableSignal<ThemeEnum> = signal<ThemeEnum>(this.themeEnum.LIGHT)

	protected isOpenSearchAssets: WritableSignal<boolean> = signal<boolean>(false)

	protected isMarket: Signal<boolean> = computed<boolean>((): boolean => {
		const marketStatus: MarketStatusInterface | null = this.marketStatus()
		return marketStatus?.status === this.marketStatusEnum.marketOpen
	})

	private _listenTheme(): void {
		this._themeService.theme$.pipe(takeUntil(this._destroy$)).subscribe((theme: ThemeEnum) => {
			this.theme.set(theme)
		})
	}

	protected setLiveTicker(item: WatchListAssetInterface): void {
		if (!this._widgetsService.hasWidgetsNeedTicker()) return
		this._widgetsService.setLiveTickerToDashboard(item.symbol)
		this._liveTickerService.launchLiveTicker(item.symbol)
	}

	protected toggleSearchAssets(value: boolean): void {
		this.isOpenSearchAssets.set(value)
	}

	protected changeSort(activeSort: ColumnSortInterface<keyof WatchListAssetInterface>): void {
		const widget: WidgetInterface<WatchListSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				activeSort: activeSort,
			},
		})
	}

	ngOnInit(): void {
		this._listenTheme()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
